import React, { useEffect, useMemo, useState } from 'react';
import { capitalize, toLower } from 'lodash';

import useScreenSize from 'Hooks/useScreenSize';
import {
	ICourseEntityAttributes, IRequirementEntityAttributes,
} from 'Models/Entities';

import CourseAccordion from 'Views/Components/CourseAccordion';
import Chip from 'Views/Components/Chip/Chip';
import ChipGroup from 'Views/Components/Chip/ChipGroup';
import CompleteCreditChip from 'Views/Components/CourseAccordion/CompleteCreditChip';

export interface DuplicateCourseItem {
	key: string,
	course: ICourseEntityAttributes,
	components: IRequirementEntityAttributes[],
	displayComponents: IRequirementEntityAttributes[],
}

interface DuplicateCourseSelectionProps {
	duplicateCourses: DuplicateCourseItem[]
	completedCourseIds: string[],
	description?: string
}

const DuplicateCourseSelection = (props: DuplicateCourseSelectionProps) => {
	const {
		duplicateCourses,
		completedCourseIds,
		description,
	} = props;

	const [collapsed, setCollapsed] = useState(false);
	const isMobile = useScreenSize();

	// on mount, on mobile, collapse the accordions by default
	useEffect(() => {
		if (isMobile) {
			setCollapsed(true);
		}
	}, [isMobile]);

	const sortedCourses = useMemo(
		() => [...duplicateCourses].sort((a, b) => a.course.code.localeCompare(b.course.code)),
		[duplicateCourses],
	);

	return (
		<>
			<h4 className="requirement-check-section-header">
				{capitalize(window.lexicon.duplicateSelectionSummary)}
			</h4>
			{
				description
					? <p>{description}</p>
					: null
			}
			<CourseAccordion.Accordion<typeof duplicateCourses[number]>
				title={`${toLower(window.lexicon.duplicate)} ${toLower(window.lexicon.courses)}`}
				id="duplicate-course-list"
				depth={0}
				collapsed={collapsed}
				setCollapsed={() => setCollapsed(prevState => !prevState)}
				items={sortedCourses}
				emptyLabel={`No ${toLower(window.lexicon.duplicate)} ${toLower(window.lexicon.courses)}`}
				renderItem={(item, index) => {
					const componentChips = item.displayComponents
						.map(comp => (
							<Chip key={comp.id} chipLabel={comp.displayName} />
						));

					const isCourseCompleted = !!completedCourseIds.find(id => id === item.course.id);

					const completionLabel = isCourseCompleted
						? <CompleteCreditChip />
						: null;

					return (
						<CourseAccordion.Item
							key={item.key}
							course={item.course}
							index={index}
							label={<ChipGroup>{componentChips}</ChipGroup>}
							additionalStatus={completionLabel}
						/>
					);
				}}
			/>
		</>
	);
};

export default DuplicateCourseSelection;
